import { useReservationList } from '@/features/reservations/hooks/useReservations';
import { useT } from '@/i18n';
import { fromApi, fromDateString } from '@/utils/datetime';
import { getDisplayStatus, getStatusColor } from '@/utils/status';
import type { Reservation } from '@restaurant/api-client';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

// ─── View ─────────────────────────────────────────────────────────────────────

/**
 * Seven-day booking overview for the week containing `date`. Each day is a
 * column: a sticky header (weekday + day number, today/selected highlighted), a
 * covers/bookings summary, and the day's reservations as compact time-ordered
 * chips. Built for the "how busy is this week?" glance a host needs when taking
 * a phone booking — the per-day day-calendar it replaces answered a question no
 * restaurant actually asked.
 *
 * Each column owns its own day query (stable 7-length loop → stable hook order),
 * so days already visited in the List/Timeline views paint instantly from cache.
 */
export default function WeekView({
  date,
  selectedId,
  onSelectReservation,
}: {
  date: string;
  selectedId: string | null;
  onSelectReservation: (reservation: Reservation) => void;
}) {
  // ISO week → Monday start, which matches the DE/EU convention the rest of the
  // app assumes. Seven date strings drive both the columns and their queries.
  const days = useMemo(() => {
    const start = fromDateString(date).startOf('week');
    return Array.from({ length: 7 }, (_, i) => start.plus({ days: i }).toISODate()!);
  }, [date]);

  return (
    <div className="rsv-week">
      <div className="rsv-week-grid">
        {days.map((day) => (
          <WeekDayColumn
            key={day}
            day={day}
            isSelected={day === date}
            selectedId={selectedId}
            onSelectReservation={onSelectReservation}
          />
        ))}
      </div>
    </div>
  );
}

function WeekDayColumn({
  day,
  isSelected,
  selectedId,
  onSelectReservation,
}: {
  day: string;
  isSelected: boolean;
  selectedId: string | null;
  onSelectReservation: (r: Reservation) => void;
}) {
  const t = useT();
  const { data } = useReservationList(day);

  const dt = fromDateString(day);
  const isToday = dt.hasSame(DateTime.now(), 'day');

  // Sort by time; a cancelled/rejected/no-show booking still shows (dimmed) so
  // the host sees the whole picture, but only live bookings feed the covers
  // count that answers "how full is this day?".
  const bookings = useMemo(() => {
    return [...(data?.data ?? [])].sort(
      (a, b) => fromApi(a.time).toMillis() - fromApi(b.time).toMillis(),
    );
  }, [data]);

  const { covers, count } = useMemo(() => {
    let covers = 0;
    let count = 0;
    for (const r of bookings) {
      const ds = getDisplayStatus(r.status, r.arrivalStatus);
      if (ds === 'cancelled' || ds === 'rejected' || ds === 'no-show') continue;
      covers += r.partySize;
      count += 1;
    }
    return { covers, count };
  }, [bookings]);

  const cw = t.reservations.week;

  return (
    <div
      className={`rsv-week-col${isToday ? ' rsv-week-col-today' : ''}${
        isSelected ? ' rsv-week-col-selected' : ''
      }`}
    >
      <div className="rsv-week-head">
        <span className="rsv-week-weekday">{dt.toFormat('ccc')}</span>
        <span className="rsv-week-daynum">{dt.toFormat('d')}</span>
        <span className="rsv-week-summary">{count > 0 ? cw.summary(count, covers) : cw.none}</span>
      </div>

      <div className="rsv-week-list">
        {bookings.map((r) => {
          const ds = getDisplayStatus(r.status, r.arrivalStatus);
          const color = getStatusColor(ds);
          const dim = ds === 'cancelled' || ds === 'rejected' || ds === 'no-show';
          return (
            <button
              key={r.id}
              type="button"
              className={`rsv-week-chip${r.id === selectedId ? ' rsv-week-chip-selected' : ''}${
                dim ? ' rsv-week-chip-dim' : ''
              }`}
              style={{ borderLeftColor: color }}
              onClick={() => onSelectReservation(r)}
              title={`${r.guest.name} · ${fromApi(r.time).toFormat('HH:mm')} · ${r.partySize}p`}
            >
              <span className="rsv-week-chip-time">{fromApi(r.time).toFormat('HH:mm')}</span>
              <span className="rsv-week-chip-name">{r.guest.name}</span>
              <span className="rsv-week-chip-party">{r.partySize}p</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
